import { useSearchParams } from 'react-router-dom';
/**
 * @page PrintReportPage
 * @description 日結報表列印頁（UI-4-4）
 * 顯示日結摘要並支援 window.print() 列印
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { safeFormatDate } from '../../lib/format';
import type { DailySessionDto, TransactionDto, AccountingCashSummary } from '@pokerroom/types';

export default function PrintReportPage() {
  const { t } = useTranslation('accounting');
  const [params] = useSearchParams();
  const sessionId = params.get('sessionId');

  const { data: session } = useQuery<DailySessionDto | null>({
    queryKey: sessionId ? ['accounting-session', sessionId] : ['accounting-active-session'],
    queryFn: () =>
      api.get<DailySessionDto | null>(
        sessionId ? `/accounting/sessions/${sessionId}` : '/accounting/sessions/active',
      ),
  });

  const { data: transactions = [] } = useQuery<TransactionDto[]>({
    queryKey: ['transactions', session?.id],
    queryFn: () => api.get<TransactionDto[]>(`/accounting/transactions?sessionId=${session!.id}`),
    enabled: !!session?.id,
  });

  const { data: summary, isError: summaryError } = useQuery<AccountingCashSummary>({
    queryKey: ['accounting-summary', session?.id],
    queryFn: () => api.get(`/accounting/sessions/${session!.id}/summary`),
    enabled: !!session?.id,
  });

  const safeAmount = (v: string) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const totalIncome = Number(summary?.totalIncome ?? 0),
    totalExpense = Number(summary?.totalExpense ?? 0);

  if (!session) {
    return <div className="p-6 text-center text-base-content/40">{t('noData')}</div>;
  }

  if (!summary)
    return <p role="status">{t(summaryError ? 'cashSummaryFailed' : 'cashSummaryLoading')}</p>;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <h1 className="text-2xl font-bold text-base-content">{t('report')}</h1>
        <button
          onClick={() => window.print()}
          className="px-4 py-2 rounded-md bg-primary text-primary-content text-sm"
        >
          {t('printReport')}
        </button>
      </div>

      {/* 報表內容（列印區域） */}
      <div className="bg-base-200 border border-base-300 rounded-xl p-6 print:border-none print:shadow-none">
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold">{t('reportTitle')}</h2>
          <p className="text-sm text-base-content/60">
            {safeFormatDate(session.sessionDate, (d) =>
              d.toLocaleDateString(undefined, { timeZone: 'UTC' }),
            )}
          </p>
        </div>

        <p className="mb-4 text-sm">
          {t('dayBases', {
            accounting: summary.accountingBusinessDate,
            zipa: summary.zipaBusinessDate,
          })}
        </p>
        <p className="mb-4 text-sm">
          {t('noncashTotals', { income: summary.nonCashIncome, expense: summary.nonCashExpense })}
        </p>
        {transactions.length === 500 && <p>{t('transactionExcerpt')}</p>}
        {/* 摘要 */}
        <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
          {[
            {
              label: t('openingBalance'),
              value: `NT$${safeAmount(session.openingBalance).toLocaleString()}`,
            },
            {
              label: t('totalIncome'),
              value: `NT$${totalIncome.toLocaleString()}`,
              color: 'text-emerald-600',
            },
            {
              label: t('totalExpense'),
              value: `NT$${totalExpense.toLocaleString()}`,
              color: 'text-red-600',
            },
            {
              label: t('closingBalance'),
              value: session.closingBalance
                ? `NT$${safeAmount(session.closingBalance).toLocaleString()}`
                : t('notSettled'),
              color: 'font-bold',
            },
          ].map(({ label, value, color }) => (
            <div key={label} className="flex justify-between border-b border-base-300 pb-2">
              <span className="text-base-content/60">{label}</span>
              <span className={`font-mono ${color ?? ''}`}>{value}</span>
            </div>
          ))}
        </div>

        {/* 收支明細 */}
        <h3 className="font-semibold text-base-content mb-3 text-sm">{t('transactionDetails')}</h3>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-base-300">
              <th className="text-left py-1.5 text-base-content/60 font-medium">{t('type')}</th>
              <th className="text-left py-1.5 text-base-content/60 font-medium">
                {t('description')}
              </th>
              <th className="text-left py-1.5 text-base-content/60 font-medium">{t('category')}</th>
              <th className="text-right py-1.5 text-base-content/60 font-medium">{t('amount')}</th>
              <th className="text-right py-1.5 text-base-content/60 font-medium">{t('time')}</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((tx) => (
              <tr key={tx.id} className="border-b border-base-300/50">
                <td className="py-1.5">
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px]
                    ${tx.type === 'INCOME' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}
                  >
                    {t(tx.type.toLowerCase() as 'income' | 'expense')}
                  </span>
                </td>
                <td className="py-1.5 text-base-content">
                  {tx.description} ({t(`cashFlow_${tx.cashFlow ?? 'CASH'}`)})
                </td>
                <td className="py-1.5 text-base-content/60">{tx.categoryName ?? '-'}</td>
                <td
                  className={`py-1.5 text-right font-mono ${tx.type === 'INCOME' ? 'text-emerald-600' : 'text-red-600'}`}
                >
                  {tx.type === 'INCOME' ? '+' : '-'}NT${safeAmount(tx.amount).toLocaleString()}
                </td>
                <td className="py-1.5 text-right text-base-content/50">
                  {safeFormatDate(tx.createdAt, (d) =>
                    d.toLocaleTimeString(undefined, {
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZone: 'Asia/Taipei',
                    }),
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-6 pt-4 border-t border-base-300 text-xs text-base-content/40 text-center">
          {t('printedAt', {
            time: new Date().toLocaleString(undefined, { timeZone: 'Asia/Taipei' }),
          })}
        </div>
      </div>
    </div>
  );
}
